import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { Timeline } from "@/components/timeline/Timeline";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import {
  ArrowLeft,
  Package,
  Calendar,
  Clock,
  MapPin,
  Truck,
  User,
  Phone,
  Shirt,
  CreditCard,
  XCircle,
  Ruler,
} from "lucide-react";
import { ReviewPrompt } from "@/components/orders/ReviewPrompt";

interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  tagCode?: string;
  conditionNotes?: string;
  status: string;
}

interface Order {
  id: string;
  publicId: string;
  orderType: string;
  status: string;
  cutoffEligible: boolean;
  promisedDeliveryAt: string | null;
  createdAt: string;
  customer: {
    id: string;
    publicId: string;
    name: string;
    phone?: string;
  };
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    pincode: string;
  };
  area: { name: string };
  items: OrderItem[];
  pickupShipment?: {
    id: string;
    status: string;
    otp?: string;
    slot: { startAt: string; endAt: string };
    rider?: { name: string; phone?: string };
  } | null;
  deliveryShipment?: {
    id: string;
    status: string;
    otp?: string;
    slot: { startAt: string; endAt: string };
    rider?: { name: string; phone?: string };
  } | null;
  quote?: {
    id: string;
    status: string;
    subtotal: number;
    tax: number;
    deliveryFee: number;
    total: number;
    lineItems: { alterationType: string; unitPrice: number; qty: number }[];
  } | null;
  measurements?: {
    id: string;
    versionNo: number;
    bustCm: number;
    waistCm: number;
    hipCm: number;
  } | null;
}

interface TimelineEntry {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  actor?: { id: string; publicId: string; name: string; role: string } | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

const statusConfig: Record<string, { color: string; label: string }> = {
  CREATED: { color: "bg-gray-100 text-gray-700", label: "Created" },
  PICKUP_SCHEDULED: { color: "bg-blue-100 text-blue-700", label: "Pickup Scheduled" },
  PICKUP_ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Pickup Assigned" },
  PICKUP_IN_PROGRESS: { color: "bg-orange-100 text-orange-700", label: "Pickup In Progress" },
  PICKED_UP: { color: "bg-teal-100 text-teal-700", label: "Picked Up" },
  RECEIVED_AT_WORKSHOP: { color: "bg-blue-100 text-blue-700", label: "At Workshop" },
  INSPECTION_IN_PROGRESS: { color: "bg-sky-100 text-sky-700", label: "Inspecting" },
  QUOTE_SENT: { color: "bg-amber-100 text-amber-700", label: "Quote Sent" },
  AWAITING_PAYMENT: { color: "bg-yellow-100 text-yellow-700", label: "Awaiting Payment" },
  PAID: { color: "bg-green-100 text-green-700", label: "Paid" },
  IN_PRODUCTION: { color: "bg-blue-100 text-blue-700", label: "In Production" },
  QC: { color: "bg-sky-100 text-sky-700", label: "Quality Check" },
  READY_FOR_DELIVERY: { color: "bg-emerald-100 text-emerald-700", label: "Ready" },
  DELIVERY_ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Delivery Assigned" },
  OUT_FOR_DELIVERY: { color: "bg-orange-100 text-orange-700", label: "Out for Delivery" },
  DELIVERED: { color: "bg-green-100 text-green-700", label: "Delivered" },
  DISPUTE_OPEN: { color: "bg-red-100 text-red-700", label: "Dispute Open" },
  CANCELLED: { color: "bg-red-100 text-red-700", label: "Cancelled" },
  RETURN_SCHEDULED: { color: "bg-orange-100 text-orange-700", label: "Return Scheduled" },
  CLOSED: { color: "bg-slate-100 text-slate-600", label: "Closed" },
};

const CANCELLABLE_STATUSES = ["CREATED", "PICKUP_SCHEDULED", "PICKUP_ASSIGNED"];

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const orderId = params.id as string;

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [hasReview, setHasReview] = useState(false);

  useEffect(() => {
    fetchData();
  }, [orderId]);

  async function fetchData() {
    try {
      const [orderRes, timelineRes, reviewRes] = await Promise.all([
        fetch(`/api/customer/orders/${orderId}`),
        fetch(`/api/timeline/order?orderId=${orderId}`),
        fetch(`/api/customer/orders/${orderId}/review`),
      ]);

      if (!orderRes.ok) throw new Error("Failed to fetch order");

      const orderData = await orderRes.json();
      setOrder(orderData.order);

      if (timelineRes.ok) {
        const timelineData = await timelineRes.json();
        setTimeline(timelineData.timeline || []);
      }

      if (reviewRes.ok) {
        const reviewData = await reviewRes.json();
        setHasReview(!!reviewData.review);
      }
    } catch (error) {
      toast.error("Failed to load order");
      navigate("/customer/orders");
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      const response = await fetch(`/api/customer/orders/${orderId}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "Customer requested cancellation" }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to cancel order");
      }

      toast.success("Order cancelled successfully");
      fetchData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cancel");
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Order Details" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-[400px] lg:col-span-2" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.CREATED;
  const canCancel = CANCELLABLE_STATUSES.includes(order.status);
  const totalItems = order.items.reduce((sum, item) => sum + item.qty, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Order ${order.publicId}`}
        actions={
          <div className="flex items-center gap-2">
            {canCancel && (
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowCancelDialog(true)}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel Order
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/customer/orders")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Orders
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{order.publicId}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {order.orderType === "ALTERATION" ? "Alteration" : "Stitching"} Order
                    </p>
                  </div>
                </div>
                <Badge className={status.color}>{status.label}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">{formatDateLong(order.createdAt)}</p>
                  </div>
                </div>
                {order.promisedDeliveryAt && (
                  <div className="flex items-start gap-3">
                    <Truck className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Est. Delivery</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {formatDateLong(order.promisedDeliveryAt)}
                        </p>
                        {order.cutoffEligible && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            Fast Track
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Pickup & Delivery Address
                  </p>
                  <p className="font-medium">{order.address.line1}</p>
                  {order.address.line2 && (
                    <p className="text-sm">{order.address.line2}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {order.address.city}, {order.address.state} -{" "}
                    {order.address.pincode}
                  </p>
                </div>
              </div>

              {order.pickupShipment && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Pickup Schedule</p>
                      <p className="font-medium">
                        {formatDateLong(order.pickupShipment.slot.startAt)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatTimeRange(
                          order.pickupShipment.slot.startAt,
                          order.pickupShipment.slot.endAt
                        )}
                      </p>
                      {order.pickupShipment.rider && (
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {order.pickupShipment.rider.name}
                            </span>
                          </div>
                          {order.pickupShipment.rider.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {order.pickupShipment.rider.phone}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {order.pickupShipment?.otp &&
                ["PICKUP_ASSIGNED", "PICKUP_IN_PROGRESS"].includes(order.status) && (
                  <>
                    <Separator />
                    <div className="p-4 bg-primary/5 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Pickup Verification OTP
                      </p>
                      <p className="text-3xl font-bold tracking-widest text-primary">
                        {order.pickupShipment.otp}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Share this code with the rider to verify pickup
                      </p>
                    </div>
                  </>
                )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shirt className="h-4 w-4" />
                Order Items ({totalItems})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{item.garmentType}</p>
                      {item.conditionNotes && (
                        <p className="text-sm text-muted-foreground">
                          {item.conditionNotes}
                        </p>
                      )}
                      {item.tagCode && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Tag: {item.tagCode}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        x{item.qty}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {item.status.replace(/_/g, " ")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {order.quote && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Quote
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/customer/orders/${orderId}/quote`)}
                  >
                    View Full Quote
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {order.quote.lineItems.map((line, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-sm"
                    >
                      <span>
                        {line.alterationType} x{line.qty}
                      </span>
                      <span>Rs. {(line.unitPrice * line.qty).toFixed(2)}</span>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>Rs. {order.quote.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span>Rs. {order.quote.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Delivery</span>
                    <span>Rs. {order.quote.deliveryFee.toFixed(2)}</span>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between font-semibold">
                    <span>Total</span>
                    <span>Rs. {order.quote.total.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {order.measurements && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Linked Measurements (v{order.measurements.versionNo})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Bust</p>
                    <p className="font-medium">{order.measurements.bustCm} cm</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Waist</p>
                    <p className="font-medium">{order.measurements.waistCm} cm</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Hip</p>
                    <p className="font-medium">{order.measurements.hipCm} cm</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(order.status === "DELIVERED" || order.status === "CLOSED") && !hasReview && (
            <ReviewPrompt
              orderId={order.id}
              orderPublicId={order.publicId}
              onReviewSubmitted={() => setHasReview(true)}
            />
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Order Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline entries={timeline} />
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title="Cancel Order"
        description="Are you sure you want to cancel this order? This action cannot be undone."
        confirmLabel="Yes, Cancel Order"
        onConfirm={handleCancel}
        loading={cancelling}
        variant="destructive"
      />
    </div>
  );
}
